import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useDoc, useCollection } from '../hooks/useFirestore';
import { Item, Image as ItemImage } from '../types/item';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Autocomplete,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function ItemEditLayout() {
  const { cid, iid } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemTags, setItemTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [itemImages, setItemImages] = useState<ItemImage[]>([]);
  const [pendingImages, setPendingImages] = useState<{file: File, preview: string}[]>([]);
  const [saving, setSaving] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Fetch the item data
  const {
    snapshot: item,
    isLoading,
    error,
  } = useDoc<Item>(`users/${userId}/collections/${cid}/items/${iid}`);

  // Fetch all items in collection for tag suggestions
  const { snapshot: allItems } = useCollection<Item>(`users/${userId}/collections/${cid}/items`);

  // Set form values when item data is loaded
  useEffect(() => {
    if (item && !initialLoadDone) {
      setItemName(item.name);
      setItemDescription(item.description || '');
      setItemTags(item.tags || []);
      
      // Handle multiple images if available
      if (item.images && item.images.length > 0) {
        setItemImages(item.images);
      } else if (item.imageUrl) {
        // Create an image entry from the legacy imageUrl field
        setItemImages([{
          url: item.imageUrl,
          createdAt: item.createdAt
        }]);
      }
      
      setInitialLoadDone(true);
    }
  }, [item, initialLoadDone]);
  
  // Extract all unique tags from items for suggestions
  useEffect(() => {
    if (allItems && allItems.length > 0) {
      const tagSet = new Set<string>();
      allItems.forEach(item => {
        if (item.tags && item.tags.length > 0) {
          item.tags.forEach(tag => tagSet.add(tag));
        }
      });
      setAvailableTags(Array.from(tagSet).sort());
    } else {
      setAvailableTags([]);
    }
  }, [allItems]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      // Create a preview URL
      const preview = URL.createObjectURL(selectedFile);
      
      // Add to pending images
      setPendingImages([...pendingImages, { file: selectedFile, preview }]);
    }
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const capturedImage = e.target.files[0];
      // Create a preview URL
      const preview = URL.createObjectURL(capturedImage);
      
      // Add to pending images
      setPendingImages([...pendingImages, { file: capturedImage, preview }]);
    }
  };
  
  const handleRemovePendingImage = (index: number) => {
    setPendingImages(pendingImages.filter((_, i) => i !== index));
  };
  
  const handleRemoveExistingImage = (index: number) => {
    setItemImages(itemImages.filter((_, i) => i !== index));
  };

  const uploadImage = async (file: File) => {
    if (!file || !userId || !cid) return null;
    
    try {
      setSaving(true);
      const storageRef = ref(storage, `users/${userId}/collections/${cid}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      return downloadUrl;
    } catch (error) {
      console.error("Error uploading image: ", error);
      return null;
    }
  };

  const handleSave = async () => {
    if (!cid || !iid || !itemName.trim()) return;
    
    try {
      setSaving(true);
      
      // Create array for new images to upload
      const newImages = [...itemImages]; // Start with existing images
      
      // Upload all pending images
      for (const pendingImage of pendingImages) {
        const imageUrl = await uploadImage(pendingImage.file);
        if (imageUrl) {
          newImages.push({
            url: imageUrl,
            createdAt: new Date() as any // Firebase Timestamp will be created when saved
          });
        }
      }
      
      // Set the main imageUrl to the first image for backward compatibility
      const mainImageUrl = newImages.length > 0 ? newImages[0].url : null;
      
      // Update the item document
      await updateDoc(doc(db, `users/${userId}/collections/${cid}/items/${iid}`), {
        name: itemName.trim(),
        description: itemDescription.trim() || '',
        imageUrl: mainImageUrl, // For backward compatibility
        images: newImages,
        tags: itemTags,
        updatedAt: new Date(),
      });
      
      // Navigate back to the item detail view
      navigate(`/h/collection/${cid}/item/${iid}`);
    } catch (error) {
      console.error("Error updating item:", error);
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          bgcolor: 'error.light', 
          color: 'error.dark',
          borderRadius: 2,
          mt: 2
        }}
      >
        <Typography>Error: {error.message}</Typography>
      </Paper>
    );
  }

  if (!item) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          borderRadius: 2,
          bgcolor: 'action.hover',
          mt: 2
        }}
      >
        <Typography variant="body1">
          Item not found. It may have been deleted or the URL is incorrect.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }} 
          onClick={() => navigate(`/h/collection/${cid}`)}
        >
          Return to Collection
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 2, md: 3 },
          px: { xs: 0.5, sm: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate(`/h/collection/${cid}/item/${iid}`)} 
            sx={{ mr: 1 }}
            aria-label="Back to item details"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h5"
            sx={{
              fontWeight: 500,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Edit Item
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!itemName.trim() || saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>

      {/* Edit Form */}
      <Paper 
        elevation={1}
        sx={{ 
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          mb: 3
        }}
      >
        <TextField
          label="Item Name"
          variant="outlined"
          fullWidth
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
          sx={{ mb: 3 }}
          required
        />

        <TextField
          label="Description"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
          sx={{ mb: 3 }}
        />

        {/* Tags section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tags
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {itemTags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => {
                  setItemTags(itemTags.filter((_, i) => i !== index));
                }}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Autocomplete
              size="small"
              freeSolo
              fullWidth
              value={tagInput}
              inputValue={tagInput}
              onInputChange={(event, newInputValue) => {
                setTagInput(newInputValue);
              }}
              onChange={(event, newValue) => {
                if (newValue && !itemTags.includes(newValue)) {
                  setItemTags([...itemTags, newValue]);
                  setTagInput('');
                }
              }}
              options={availableTags.filter(tag => !itemTags.includes(tag))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add a tag"
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && tagInput.trim()) {
                      e.preventDefault();
                      if (!itemTags.includes(tagInput.trim())) {
                        setItemTags([...itemTags, tagInput.trim()]);
                      }
                      setTagInput('');
                    }
                  }}
                />
              )}
            />
            <Button 
              variant="outlined"
              size="small"
              onClick={() => {
                if (tagInput.trim() && !itemTags.includes(tagInput.trim())) {
                  setItemTags([...itemTags, tagInput.trim()]);
                  setTagInput('');
                }
              }}
              disabled={!tagInput.trim()}
            >
              Add
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Hidden file inputs */}
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleImageSelect}
        />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          ref={cameraInputRef}
          onChange={handleCameraCapture}
        />

        {/* Images section */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Item Images
        </Typography>

        {/* Image upload buttons */}
        <Box 
          sx={{ 
            display: 'flex',
            gap: 2,
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
            sx={{ flex: { sm: 1 } }}
          >
            Upload Image
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PhotoCameraIcon />}
            onClick={() => cameraInputRef.current?.click()}
            disabled={saving}
            sx={{ flex: { sm: 1 } }}
          >
            Take Photo
          </Button>
        </Box>
        
        {/* Existing images */}
        {(itemImages.length > 0 || pendingImages.length > 0) ? (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Images ({itemImages.length + pendingImages.length})
            </Typography>
            <ImageList 
              sx={{ 
                width: '100%',
                maxHeight: 450,
                mb: 3
              }}
              cols={3}
              rowHeight={160}
              gap={8}
            >
              {/* Existing images */}
              {itemImages.map((image, index) => (
                <ImageListItem key={`existing-${index}`}>
                  <img
                    src={image.url}
                    alt={`Item ${index + 1}`}
                    loading="lazy"
                    style={{ objectFit: 'contain', height: '100%' }}
                  />
                  <ImageListItemBar
                    sx={{
                      background: 'rgba(0, 0, 0, 0.4)'
                    }}
                    position="top"
                    actionIcon={
                      <IconButton
                        sx={{ color: 'white' }}
                        onClick={() => handleRemoveExistingImage(index)}
                        disabled={saving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  />
                </ImageListItem>
              ))}
              
              {/* Pending images */}
              {pendingImages.map((image, index) => (
                <ImageListItem key={`pending-${index}`}>
                  <img
                    src={image.preview}
                    alt={`Pending upload ${index + 1}`}
                    loading="lazy"
                    style={{ objectFit: 'contain', height: '100%' }}
                  />
                  <ImageListItemBar
                    sx={{
                      background: 'rgba(0, 0, 0, 0.4)'
                    }}
                    title="New"
                    position="top"
                    actionIcon={
                      <IconButton
                        sx={{ color: 'white' }}
                        onClick={() => handleRemovePendingImage(index)}
                        disabled={saving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              p: 4,
              borderRadius: 1,
              bgcolor: 'rgba(0, 0, 0, 0.03)',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <Typography color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
              No images added yet
            </Typography>
          </Paper>
        )}
      </Paper>
    </Box>
  );
}